import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/styles';
import { Link, useLocation } from 'react-router-dom';
import AnchorLink from 'react-anchor-link-smooth-scroll';
import {
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  AdminPanelSettings as AdminIcon,
  Assessment as ReportsIcon,
  Tune as ProcessingIcon,
  Home as HomeIcon,
  HelpOutline as HelpIcon,
  Star as StarIcon,
  Info as InfoIcon,
  AccountBalance as BankingIcon,
  CurrencyExchange,
  FileCopy as FileIcon,
  Security as SecurityIcon,
  MonetizationOn as ArrecadacaoIcon,
  BarChart as GestaoLimiteIcon,
  Groups as ParticipantesIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

import { isUserAdmin, isUserCustomerOperator, isUserFinancier } from 'utils/auth';
import { useWebPush } from 'hooks/useWebPush';
import { ReactComponent as TorqiLogo } from 'assets/logo_white.svg';
import { ReactComponent as SemPararLogo } from 'assets/logo_semparar.svg';
import CargillLogo from 'assets/logo_cargill.png';
import { ReactComponent as PaymentsIcon } from 'assets/icons/Payments.svg';
import { ReactComponent as ReportsIconSvg } from 'assets/icons/Reports.svg';
import { ReactComponent as SendAndContractsIcon } from 'assets/icons/SendAndContracts.svg';
import { ESubdomain, getSubdomain } from 'utils/url';
import { navBarItems, NAVBAR_HEIGHT } from 'config/navbar';
import { AccountService, CarrierService, GuaranteeService } from 'services';
import { aggregateFinancierMenuState } from 'utils/financier-menu';
import { EFeatureAvailability } from 'common/carrier.interface';
import useStyles from '../styles';

interface BaseLayoutProps {
  type: 'app' | 'landing' | 'none';
  loginButton?: any;
  onLogout?: () => void;
  children: any;
  hideButtons?: boolean;
}

const SIDEBAR_WIDTH = 280;
const SIDEBAR_COLLAPSED_WIDTH = 90;

const BaseLayout = ({ type, children, loginButton, onLogout, hideButtons = true }: BaseLayoutProps) => {
  const classes = useStyles();
  const theme = useTheme<any>();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAdminUser = isUserAdmin();
  const isFinancierUser = isUserFinancier();
  const isAdminOrFinancierUser = isAdminUser || isFinancierUser;
  const navItems: any[] = isFinancierUser && type === 'app' ? navBarItems.financier : navBarItems[type];
  const [itemSelected, setItemSelected] = useState('#main');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bankingItemState, setBankingState] = useState<EFeatureAvailability>(EFeatureAvailability.HIDDEN);
  const [fileItemState, setFileItemState] = useState<EFeatureAvailability>(EFeatureAvailability.HIDDEN);
  const [anticipationMenuState, setAnticipationMenuState] = useState<EFeatureAvailability>(EFeatureAvailability.HIDDEN);
  const [guaranteeMenuState, setGuaranteeMenuState] = useState<EFeatureAvailability>(EFeatureAvailability.HIDDEN);
  const [reportsMenuState, setReportsMenuState] = useState<EFeatureAvailability>(EFeatureAvailability.HIDDEN);
  const [hasGuaranteeSettings, setHasGuaranteeSettings] = useState(false);
  const [carrierRootId, setCarrierRootId] = useState<number | null>(null);
  const [financierArrecadacaoState, setFinancierArrecadacaoState] = useState<EFeatureAvailability>(EFeatureAvailability.ENABLED);
  const [financierGestaoLimiteState, setFinancierGestaoLimiteState] = useState<EFeatureAvailability>(EFeatureAvailability.ENABLED);
  const [financierReportsState, setFinancierReportsState] = useState<EFeatureAvailability>(EFeatureAvailability.ENABLED);
  const [financierFileState, setFinancierFileState] = useState<EFeatureAvailability>(EFeatureAvailability.ENABLED);

  useWebPush({ carrierRootId, isAdmin: isAdminUser });

  const subdomain = getSubdomain();
  const isSemParar = subdomain === ESubdomain.SEM_PARAR;
  const isCargill = subdomain === ESubdomain.CARGILL;

  const sidebarBgColor: string = theme.customColors.sidebar.background;
  const sidebarTextColor: string = theme.customColors.sidebar.textColor;

  const { hoverBg: navItemHoverBgColor, selectedBg, selectedTextColor, iconColor } = theme.customColors.sidebar;

  const renderLogo = (width: string, height: string) => {
    if (isSemParar) return <SemPararLogo width={width} height={height} />;
    if (isCargill) return <img src={CargillLogo} alt="Cargill" style={{ width, height, objectFit: 'contain' }} />;
    return <TorqiLogo width={width} height={height} />;
  };

  const getNavItemBgColor = (isSelected: boolean) => (isSelected ? selectedBg : 'transparent');
  const getIconColor = (isSelected: boolean) => (isSelected ? selectedTextColor : iconColor);
  const getTextColor = (isSelected: boolean) => (isSelected ? selectedTextColor : sidebarTextColor);

  useEffect(() => {
    const fetchCarrierInfo = async () => {
      try {
        const accountService = new AccountService();
        const carrierService = new CarrierService();
        const guaranteeService = new GuaranteeService();

        const userInfo = await accountService.getMe();

        const linkedFinanciers = userInfo.financiers || [];
        if (linkedFinanciers.length) {
          setFinancierArrecadacaoState(aggregateFinancierMenuState(linkedFinanciers.map((item) => item.arrecadacaoMenuState)));
          setFinancierGestaoLimiteState(aggregateFinancierMenuState(linkedFinanciers.map((item) => item.gestaoLimiteMenuState)));
          setFinancierReportsState(aggregateFinancierMenuState(linkedFinanciers.map((item) => item.reportsMenuState)));
          setFinancierFileState(aggregateFinancierMenuState(linkedFinanciers.map((item) => item.fileMenuState)));
        }

        if (userInfo.carrierRootId) {
          setCarrierRootId(userInfo.carrierRootId);
          const carrierRoot = await carrierService.getCarrierRoot(userInfo.carrierRootId);

          if (carrierRoot) {
            setBankingState(carrierRoot.bankingMenuState);
            setFileItemState(carrierRoot.fileMenuState);
            setAnticipationMenuState(carrierRoot.anticipationMenuState);
            setGuaranteeMenuState(carrierRoot.guaranteeMenuState);
            setReportsMenuState(carrierRoot.reportsMenuState);
          }
        }

        try {
          const guaranteeResponse = await guaranteeService.listSettings({ page: 1, limit: 1 });
          setHasGuaranteeSettings(guaranteeResponse.items.length > 0);
        } catch {
          setHasGuaranteeSettings(false);
        }
      } catch (error) {
        // Silent fail - if we can't fetch the info, Banking won't show
      }
    };

    if (type === 'app') {
      fetchCarrierInfo();
    }
  }, [type]);

  const getIconForNavItem = (href: string) => {
    if (href === '/contratar') {
      return isSemParar ? <SendAndContractsIcon width="24px" height="24px" /> : <DashboardIcon />;
    }
    if (href === '/garantia') return <SecurityIcon />;
    if (href === '/consulta-contratos') {
      return isSemParar ? <PaymentsIcon width="24px" height="24px" /> : <AssignmentIcon />;
    }
    if (href === '/admin') return <AdminIcon />;
    if (href === '/relatorios') {
      return isSemParar ? <ReportsIconSvg width="24px" height="24px" /> : <ReportsIcon />;
    }
    if (href === '/banking') return <BankingIcon />;
    if (href === '/retirada-manual') return <CurrencyExchange />;
    if (href === '#main') return <HomeIcon />;
    if (href === '#como-funciona') return <HelpIcon />;
    if (href === '#vantagens') return <StarIcon />;
    if (href === '#sobre') return <InfoIcon />;
    if (href === '/documentacao') return <FileIcon />;
    if (href === '/admin/processamento-propostas') return <ProcessingIcon />;
    if (href === '/admin/arrecadacao' || href === '/financier/arrecadacao/vencimento') return <ArrecadacaoIcon />;
    if (href === '/admin/gestao-limite' || href === '/financier/gestao-limite') return <GestaoLimiteIcon />;
    if (href === '/admin/transportadoras') return <ParticipantesIcon />;
    if (href === '/financier/relatorios/painel-garantia') return <ReportsIcon />;
    if (href === '/financier/documentacao/gerenciamento') return <FileIcon />;
    return <DashboardIcon />;
  };

  const isNavItemDisabled = (href: string): boolean => {
    if (href === '/banking') return bankingItemState === EFeatureAvailability.DISABLED;
    if (href === '/documentacao') return fileItemState === EFeatureAvailability.DISABLED;
    if (href === '/contratar') return anticipationMenuState === EFeatureAvailability.DISABLED;
    if (href === '/garantia') return guaranteeMenuState === EFeatureAvailability.DISABLED;
    if (href === '/relatorios') return reportsMenuState === EFeatureAvailability.DISABLED;
    if (href === '/financier/arrecadacao/vencimento') return financierArrecadacaoState === EFeatureAvailability.DISABLED;
    if (href === '/financier/gestao-limite') return financierGestaoLimiteState === EFeatureAvailability.DISABLED;
    if (href === '/financier/relatorios/painel-garantia') return financierReportsState === EFeatureAvailability.DISABLED;
    if (href === '/financier/documentacao/gerenciamento') return financierFileState === EFeatureAvailability.DISABLED;
    return false;
  };

  if (isMobile) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Box
            className={classes.navbar}
            zIndex={99}
            width="100%"
            height={75}
            padding="10px"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            position="fixed"
          >
            <Grid container alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
              <Grid item height={25}>
                <Link to="/">{renderLogo('100%', '100%')}</Link>
              </Grid>
              {loginButton}
            </Grid>
          </Box>
        </Grid>
        <Grid mt={`${NAVBAR_HEIGHT}px`} item height={`calc(100vh - ${NAVBAR_HEIGHT}px)`} xs={12}>
          {children}
        </Grid>
      </Grid>
    );
  }

  const shouldHideSidebar = location.pathname === '/finalizar-cadastro';

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {!shouldHideSidebar && (
        <Drawer
          variant="permanent"
          onMouseEnter={() => setSidebarOpen(true)}
          onMouseLeave={() => setSidebarOpen(false)}
          sx={{
            width: sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
              boxSizing: 'border-box',
              backgroundColor: sidebarBgColor,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
              minHeight: '80px',
            }}
          >
            {sidebarOpen && (
              <Collapse in={sidebarOpen} orientation="horizontal">
                <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
                  {renderLogo('160px', '55px')}
                </Link>
              </Collapse>
            )}
            {!sidebarOpen && (
              <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
                {renderLogo('32px', '32px')}
              </Link>
            )}
          </Box>

          <List sx={{ paddingTop: '24px' }}>
            {navItems.length > 0 &&
              hideButtons &&
              navItems
                .filter(({ href }) => !(!isAdminUser && href === '/admin'))
                .filter(({ href }) => !(!isAdminUser && href === '/admin/processamento-propostas'))
                .filter(({ href }) => !(!isAdminOrFinancierUser && href === '/admin/arrecadacao'))
                .filter(({ href }) => !(!isAdminOrFinancierUser && href === '/admin/gestao-limite'))
                .filter(({ href }) => !(!isAdminUser && href === '/admin/transportadoras'))
                .filter(({ href }) => !(!isAdminUser && href === '/operacoes-secundarias'))
                .filter(({ href }) => !(!isAdminUser && href === '/retirada-manual'))
                .filter(({ href }) => !(isAdminUser && href === '/contratar'))
                .filter(({ href }) => !(!isAdminUser && href === '/contratar' && anticipationMenuState === EFeatureAvailability.HIDDEN))
                .filter(({ href }) => !(isAdminUser && href === '/garantia'))
                .filter(({ href }) => !(!isAdminUser && href === '/garantia' && !hasGuaranteeSettings))
                .filter(({ href }) => !(!isAdminUser && href === '/garantia' && guaranteeMenuState === EFeatureAvailability.HIDDEN))
                .filter(({ href }) => !(!isAdminUser && href === '/relatorios' && reportsMenuState === EFeatureAvailability.HIDDEN))
                .filter(({ href }) => !(isUserCustomerOperator() && href === '/consulta-contratos'))
                .filter(({ href }) => !(isUserCustomerOperator() && href === '/banking'))
                .filter(({ href }) => !(href === '/banking' && bankingItemState === EFeatureAvailability.HIDDEN))
                .filter(
                  ({ href }) => isAdminOrFinancierUser || !(href === '/documentacao' && fileItemState === EFeatureAvailability.HIDDEN)
                )
                .filter(
                  ({ href }) => !(href === '/financier/arrecadacao/vencimento' && financierArrecadacaoState === EFeatureAvailability.HIDDEN)
                )
                .filter(({ href }) => !(href === '/financier/gestao-limite' && financierGestaoLimiteState === EFeatureAvailability.HIDDEN))
                .filter(
                  ({ href }) => !(href === '/financier/relatorios/painel-garantia' && financierReportsState === EFeatureAvailability.HIDDEN)
                )
                .filter(
                  ({ href }) => !(href === '/financier/documentacao/gerenciamento' && financierFileState === EFeatureAvailability.HIDDEN)
                )
                .map(({ label, href }) => {
                  const isNavItemSelected: boolean =
                    type === 'app' ? location.pathname === href || location.pathname.startsWith(`${href}/`) : itemSelected === href;
                  const icon = getIconForNavItem(href);

                  if (type === 'app') {
                    return (
                      <ListItem key={label} disablePadding>
                        <ListItemButton
                          disabled={isNavItemDisabled(href)}
                          component={Link}
                          to={href}
                          sx={{
                            minHeight: 48,
                            justifyContent: sidebarOpen ? 'initial' : 'center',
                            px: 2.5,
                            mx: isCargill ? 2 : 0,
                            borderRadius: isCargill ? '8px' : 0,
                            backgroundColor: getNavItemBgColor(isNavItemSelected),
                            '&:hover': {
                              backgroundColor: navItemHoverBgColor,
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: sidebarOpen ? 1 : 'auto',
                              ml: sidebarOpen ? 0 : 'auto',
                              justifyContent: 'center',
                              color: getIconColor(isNavItemSelected),
                            }}
                          >
                            {icon}
                          </ListItemIcon>
                          <Collapse in={sidebarOpen} orientation="horizontal">
                            <ListItemText
                              primary={label}
                              sx={{
                                color: getTextColor(isNavItemSelected),
                                ml: 2,
                                '& .MuiTypography-root': {
                                  fontWeight: isNavItemSelected ? 700 : 400,
                                  whiteSpace: 'nowrap',
                                },
                              }}
                            />
                          </Collapse>
                        </ListItemButton>
                      </ListItem>
                    );
                  }

                  return (
                    <ListItem key={label} disablePadding>
                      <ListItemButton
                        component={AnchorLink}
                        href={href}
                        onClick={() => setItemSelected(href)}
                        offset={0}
                        sx={{
                          minHeight: 48,
                          justifyContent: sidebarOpen ? 'initial' : 'center',
                          px: 2.5,
                          backgroundColor: isNavItemSelected ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                          '&:hover': {
                            backgroundColor: navItemHoverBgColor,
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: sidebarOpen ? 1 : 'auto',
                            ml: sidebarOpen ? 0 : 'auto',
                            justifyContent: 'center',
                            color: 'white',
                          }}
                        >
                          {icon}
                        </ListItemIcon>
                        <Collapse in={sidebarOpen} orientation="horizontal">
                          <ListItemText
                            primary={label}
                            sx={{
                              color: getTextColor(isNavItemSelected),
                              ml: 2,
                              '& .MuiTypography-root': {
                                fontWeight: isNavItemSelected ? 700 : 400,
                                whiteSpace: 'nowrap',
                              },
                            }}
                          />
                        </Collapse>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
          </List>

          <Box sx={{ flexGrow: 1 }} />

          {type === 'app' && onLogout && (
            <Box sx={{ mb: 2 }}>
              {sidebarOpen && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    px: isCargill ? 2 : 1.5,
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    fullWidth
                    onClick={onLogout}
                    sx={{
                      fontWeight: 600,
                      borderRadius: '50px',
                      width: 'fit-content',
                      backgroundColor: theme.palette.secondary.main,
                      color: '#fff',
                      '&:hover': { backgroundColor: theme.palette.secondary.dark },
                    }}
                  >
                    Sair
                  </Button>
                  <Typography variant="caption" sx={{ color: theme.palette.secondary.main, whiteSpace: 'nowrap', fontWeight: 500 }}>
                    Powered by Torqi
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          overflow: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default BaseLayout;
